import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FORM_FIELD_TYPES,
  type FormFieldDefinition,
  type FormFieldType,
  type FormTemplateResponse,
} from '@emr/shared';

import {
  archiveFormTemplate,
  createFormTemplate,
  fetchFormTemplates,
  publishFormTemplateVersion,
} from '../api/forms.ts';
import { Button, ErrorText, Field, Panel, TextAreaField } from '../components/primitives.tsx';
import { DataTable, type DataTableColumn } from '../components/DataTable.tsx';
import { GatedRoute } from '../components/GatedRoute.tsx';
import { QuerySection } from '../components/QuerySection.tsx';
import { formatDateTime } from '../lib/dateFormat.ts';

/**
 * The template-builder admin page (`BACKLOG.md` C10b) — `org:manage_settings` only, matching
 * `formTemplatesRoutes.ts`'s own write-route gate. Add/remove/reorder fields of the five supported
 * types (text/choice/numeric/date/checkbox); "Save" either creates a brand-new template family (no
 * family selected) or publishes the next version of the family currently loaded into the builder —
 * never an in-place edit, per this feature's own append-only versioning design
 * (`@emr/shared`'s `formTemplates.ts` header).
 *
 * Deliberately excluded from this builder (`BACKLOG.md` C10b's own explicit deferral list):
 * conditional/branching logic between fields, a template marketplace, and AI-assisted authoring.
 */
export function FormTemplatesPage() {
  const { t } = useTranslation('forms');
  return (
    <GatedRoute title={t('templates.pageTitle')} capability="org:manage_settings" feature={null}>
      <TemplatesSection />
    </GatedRoute>
  );
}

function newField(type: FormFieldType): FormFieldDefinition {
  const base = { key: '', label: '', required: false, helpText: null };
  switch (type) {
    case 'text':
      return { type: 'text', ...base, maxLength: null };
    case 'choice':
      return { type: 'choice', ...base, choices: [''] };
    case 'numeric':
      return { type: 'numeric', ...base, min: null, max: null };
    case 'date':
      return { type: 'date', ...base };
    case 'checkbox':
      return { type: 'checkbox', ...base };
  }
}

function TemplatesSection() {
  const { t } = useTranslation('forms');
  const queryClient = useQueryClient();
  const queryKey = ['form-templates'];

  const query = useQuery<readonly FormTemplateResponse[]>({
    queryKey,
    queryFn: ({ signal }) => fetchFormTemplates(signal),
  });

  const [editingFamilyId, setEditingFamilyId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fields, setFields] = useState<FormFieldDefinition[]>([]);

  function startNew() {
    setEditingFamilyId(null);
    setName('');
    setDescription('');
    setFields([]);
  }

  function startEditing(template: FormTemplateResponse) {
    setEditingFamilyId(template.templateFamilyId);
    setName(template.name);
    setDescription(template.description ?? '');
    setFields(template.fields.map((field) => ({ ...field })));
  }

  const save = useMutation({
    mutationFn: () => {
      const input = { name, description: description === '' ? null : description, fields };
      return editingFamilyId === null
        ? createFormTemplate(input)
        : publishFormTemplateVersion(editingFamilyId, input);
    },
    onSuccess: () => {
      startNew();
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const archive = useMutation({
    mutationFn: (templateFamilyId: string) => archiveFormTemplate(templateFamilyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const columns: readonly DataTableColumn<FormTemplateResponse>[] = [
    { key: 'name', header: t('templates.columns.name'), cell: (row) => row.name, maxWidthCh: 40 },
    {
      key: 'version',
      header: t('templates.columns.version'),
      cell: (row) => t('templates.versionBadge', { version: row.version }),
      wrap: 'nowrap',
    },
    {
      key: 'fieldCount',
      header: t('templates.columns.fieldCount'),
      cell: (row) => row.fields.length,
      wrap: 'nowrap',
    },
    {
      key: 'createdAt',
      header: t('templates.columns.createdAt'),
      cell: (row) => formatDateTime(row.createdAt),
      wrap: 'nowrap',
    },
    {
      key: 'actions',
      header: t('templates.columns.actions'),
      cardHidesLabel: true,
      wrap: 'nowrap',
      cell: (row) => (
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              startEditing(row);
            }}
          >
            {t('templates.editButton')}
          </Button>
          <Button
            variant="secondary"
            pending={archive.isPending}
            onClick={() => {
              archive.mutate(row.templateFamilyId);
            }}
          >
            {t('templates.archiveButton')}
          </Button>
        </div>
      ),
    },
  ];

  const canSave = name.trim() !== '' && fields.length > 0 && fields.every(fieldIsValid);

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-sm text-ink-muted">{t('templates.description')}</p>

      <Panel title={t('templates.listTitle')}>
        <QuerySection
          query={query}
          loadingLabel={t('common.loading')}
          isEmpty={(data) => data.length === 0}
          emptyMessage={t('templates.emptyMessage')}
        >
          {(templates) => (
            <DataTable
              caption={t('templates.listTitle')}
              columns={columns}
              rows={templates}
              rowKey={(row) => row.id}
              minWidthRem={44}
              emptyMessage={t('templates.emptyMessage')}
            />
          )}
        </QuerySection>
      </Panel>

      <Panel
        title={
          editingFamilyId === null
            ? t('templates.builder.newTitle')
            : t('templates.builder.editTitle')
        }
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (canSave) save.mutate();
          }}
          className="flex flex-col gap-4"
        >
          <Field
            label={t('templates.builder.nameLabel')}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            required
          />
          <TextAreaField
            label={t('templates.builder.descriptionLabel')}
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
            rows={2}
          />

          <div className="flex flex-col gap-3">
            <span className="text-sm font-medium text-ink">
              {t('templates.builder.fieldsLabel')}
            </span>
            {fields.length === 0 ? (
              <p className="m-0 text-sm text-ink-muted">{t('templates.builder.noFields')}</p>
            ) : null}
            {fields.map((field, index) => (
              <FieldEditor
                // Index is stable for this list's own lifetime (fields are only ever appended,
                // reordered, or removed by this same array, never independently re-keyed by an id
                // the server assigns) — the same reasoning a form-builder's in-progress draft list
                // has no server identity to key on yet.
                key={index}
                field={field}
                index={index}
                total={fields.length}
                onChange={(next) => {
                  setFields((current) => current.map((f, i) => (i === index ? next : f)));
                }}
                onRemove={() => {
                  setFields((current) => current.filter((_, i) => i !== index));
                }}
                onMove={(direction) => {
                  setFields((current) => {
                    const target = index + direction;
                    if (target < 0 || target >= current.length) return current;
                    const next = [...current];
                    const a = next[index];
                    const b = next[target];
                    if (a === undefined || b === undefined) return current;
                    next[index] = b;
                    next[target] = a;
                    return next;
                  });
                }}
              />
            ))}

            <AddFieldControl
              onAdd={(type) => {
                setFields((current) => [...current, newField(type)]);
              }}
            />
          </div>

          <div className="flex items-center gap-2 border-t border-row-line pt-4">
            <Button type="submit" pending={save.isPending} disabled={!canSave}>
              {editingFamilyId === null
                ? t('templates.builder.createButton')
                : t('templates.builder.publishButton')}
            </Button>
            {editingFamilyId !== null ? (
              <Button variant="secondary" onClick={startNew}>
                {t('templates.builder.cancelButton')}
              </Button>
            ) : null}
          </div>
          {save.isError ? <ErrorText message={t('templates.builder.saveError')} /> : null}
        </form>
      </Panel>
    </div>
  );
}

/** A field is well-formed enough to save: a non-empty key/label, and — for a choice field — at
 * least one non-empty choice. The server re-validates the full closed schema regardless; this is
 * just what keeps the "Save" button honest about an obviously incomplete draft. */
function fieldIsValid(field: FormFieldDefinition): boolean {
  if (field.key.trim() === '' || field.label.trim() === '') return false;
  if (field.type === 'choice') return field.choices.some((choice) => choice.trim() !== '');
  return true;
}

function AddFieldControl({ onAdd }: { readonly onAdd: (type: FormFieldType) => void }) {
  const { t } = useTranslation('forms');
  const [type, setType] = useState<FormFieldType>('text');
  return (
    <div className="flex flex-wrap items-end gap-2">
      <label className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-ink">{t('templates.builder.addFieldLabel')}</span>
        <select
          value={type}
          onChange={(event) => {
            setType(event.target.value as FormFieldType);
          }}
          className="rounded-control border border-field-line bg-field px-3 py-2 text-base text-ink sm:text-sm"
        >
          {FORM_FIELD_TYPES.map((fieldType) => (
            <option key={fieldType} value={fieldType}>
              {t(`templates.fieldTypes.${fieldType}`)}
            </option>
          ))}
        </select>
      </label>
      <Button
        variant="secondary"
        type="button"
        onClick={() => {
          onAdd(type);
        }}
      >
        {t('templates.builder.addFieldButton')}
      </Button>
    </div>
  );
}

function FieldEditor({
  field,
  index,
  total,
  onChange,
  onRemove,
  onMove,
}: {
  readonly field: FormFieldDefinition;
  readonly index: number;
  readonly total: number;
  readonly onChange: (next: FormFieldDefinition) => void;
  readonly onRemove: () => void;
  readonly onMove: (direction: -1 | 1) => void;
}) {
  const { t } = useTranslation('forms');

  return (
    <div className="flex flex-col gap-3 rounded-control border border-line p-3 sm:flex-row sm:flex-wrap sm:items-end">
      <span className="text-xs font-medium text-ink-muted sm:self-start sm:pt-2">
        {t(`templates.fieldTypes.${field.type}`)}
      </span>
      <Field
        label={t('templates.builder.fieldKeyLabel')}
        value={field.key}
        onChange={(event) => {
          onChange({ ...field, key: event.target.value });
        }}
        hint={t('templates.builder.fieldKeyHint')}
        required
      />
      <Field
        label={t('templates.builder.fieldLabelLabel')}
        value={field.label}
        onChange={(event) => {
          onChange({ ...field, label: event.target.value });
        }}
        required
      />
      <label className="flex items-center gap-2 text-sm text-ink">
        <input
          type="checkbox"
          checked={field.required}
          onChange={(event) => {
            onChange({ ...field, required: event.target.checked });
          }}
        />
        {t('templates.builder.fieldRequiredLabel')}
      </label>

      {field.type === 'choice' ? (
        <Field
          label={t('templates.builder.choicesLabel')}
          value={field.choices.join(', ')}
          onChange={(event) => {
            onChange({
              ...field,
              choices: event.target.value.split(',').map((choice) => choice.trim()),
            });
          }}
          hint={t('templates.builder.choicesHint')}
        />
      ) : null}
      {field.type === 'text' ? (
        <Field
          label={t('templates.builder.maxLengthLabel')}
          type="number"
          min={1}
          value={field.maxLength ?? ''}
          onChange={(event) => {
            onChange({
              ...field,
              maxLength: event.target.value === '' ? null : Number(event.target.value),
            });
          }}
        />
      ) : null}
      {field.type === 'numeric' ? (
        <>
          <Field
            label={t('templates.builder.minLabel')}
            type="number"
            value={field.min ?? ''}
            onChange={(event) => {
              onChange({
                ...field,
                min: event.target.value === '' ? null : Number(event.target.value),
              });
            }}
          />
          <Field
            label={t('templates.builder.maxLabel')}
            type="number"
            value={field.max ?? ''}
            onChange={(event) => {
              onChange({
                ...field,
                max: event.target.value === '' ? null : Number(event.target.value),
              });
            }}
          />
        </>
      ) : null}

      <div className="flex gap-1">
        <Button
          type="button"
          variant="secondary"
          disabled={index === 0}
          onClick={() => {
            onMove(-1);
          }}
          aria-label={t('templates.builder.moveUp')}
        >
          ↑
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={index === total - 1}
          onClick={() => {
            onMove(1);
          }}
          aria-label={t('templates.builder.moveDown')}
        >
          ↓
        </Button>
        <Button type="button" variant="secondary" onClick={onRemove}>
          {t('templates.builder.removeField')}
        </Button>
      </div>
    </div>
  );
}
